import { useState } from 'react';
import { AlertOctagon, Calendar, FileDown, Plus, RefreshCw, Wrench, Zap } from 'lucide-react';
import {
  useAlertesPlanning, useAnnulerPlanning, useBonsTravail, useCamions,
  useGenererBTPlanning, useMaintenanceDashboard, usePlanning,
  useRapportMensuelMaintenance, useTriggerN2Hebdo,
} from '@/hooks/useTransport';
import { useAuthStore } from '@/stores/authStore';
import { demander, informer } from '@/components/ui/Fenetres';
import { BonTravailDialog } from '@/components/operations/transport/BonTravailDialog';
import { generateRapportMensuelMaintenancePDF } from '@/utils/rapport-mensuel-maintenance-pdf';
import {
  NIVEAU_BT_LABELS, NIVEAU_COLORS, STATUT_BT_COLORS, STATUT_BT_LABELS,
  STATUT_PLANNING_LABELS, TYPE_BT_LABELS,
  type BonTravail, type NiveauBonTravail, type StatutBonTravail,
  type StatutPlanningPreventif, type TypeBonTravail,
} from '@/types';
import { can } from '@/utils/permissions';

const fmtInt = (n: number) =>
  new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 })
    .format(n)
    .replace(/[  ]/g, ' ');

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });

const ageFromCreated = (iso: string) => {
  const diffMs = Date.now() - new Date(iso).getTime();
  const h = Math.floor(diffMs / 3600_000);
  if (h < 1) return '< 1h';
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}j`;
};

export const MaintenancePage = () => {
  const role = useAuthStore((s) => s.user?.role);
  const [tab, setTab] = useState<'dashboard' | 'bt' | 'planning' | 'rapport'>('dashboard');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editBT, setEditBT] = useState<BonTravail | undefined>(undefined);

  const canManage = can(role, 'operations.manage');

  return (
    <div className="ds-content">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <h1 style={{ fontFamily: 'var(--font-t)', fontSize: 22, fontWeight: 700, color: 'var(--p-deep)', margin: 0, flex: 1 }}>
          Maintenance
        </h1>
        {canManage && (
          <button type="button" onClick={() => { setEditBT(undefined); setDialogOpen(true); }} className="ds-btn ds-btn-p" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Plus size={14} /> Créer un BT
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--bdr)', marginBottom: 14 }}>
        {[
          { id: 'dashboard', label: 'Tableau de bord' },
          { id: 'bt',        label: 'Bons de Travail' },
          { id: 'planning',  label: 'Planning préventif' },
          { id: 'rapport',   label: 'Rapport mensuel' },
        ].map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id as 'dashboard' | 'bt' | 'planning' | 'rapport')} style={{
            padding: '8px 16px', background: 'transparent', border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: 600,
            color: tab === t.id ? 'var(--p)' : 'var(--tx-3)',
            borderBottom: tab === t.id ? '2px solid var(--p)' : '2px solid transparent',
            marginBottom: -1,
          }}>{t.label}</button>
        ))}
      </div>

      {tab === 'dashboard' && <DashboardTab />}
      {tab === 'bt'        && <BTTab onEdit={(bt) => { setEditBT(bt); setDialogOpen(true); }} />}
      {tab === 'planning'  && <PlanningTab />}
      {tab === 'rapport'   && <RapportMensuelTab />}

      <BonTravailDialog open={dialogOpen} onClose={() => setDialogOpen(false)} bt={editBT} />
    </div>
  );
};

// ── Onglet Rapport mensuel maintenance ─────────────────────────────────────

const RapportMensuelTab = () => {
  const now = new Date();
  const [year, setYear]   = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [exporting, setExporting] = useState(false);
  const { data, isLoading } = useRapportMensuelMaintenance(year, month);

  const onExport = async () => {
    if (!data) return;
    try { setExporting(true); await generateRapportMensuelMaintenancePDF(data); }
    finally { setExporting(false); }
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 12, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <Field label="Année">
          <input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} className="ds-fi" style={{ width: 100 }} />
        </Field>
        <Field label="Mois">
          <select value={month} onChange={(e) => setMonth(parseInt(e.target.value, 10))} className="ds-fi" style={{ width: 150 }}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleDateString('fr-FR', { month: 'long' })}</option>
            ))}
          </select>
        </Field>
        <div style={{ flex: 1 }} />
        <button type="button" className="ds-btn ds-btn-p" onClick={onExport} disabled={!data || exporting} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <FileDown size={14} /> {exporting ? 'Génération…' : 'Exporter PDF'}
        </button>
      </div>

      {isLoading || !data ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--tx-3)' }}>Chargement…</div>
      ) : (
        <>
          {/* Synthèse */}
          <div className="ds-bp" style={{ marginBottom: 14 }}>
            <div className="ds-bp-h">
              <div>
                <div className="ds-bp-t">Synthèse — {data.periode.mois}</div>
                <div className="ds-bp-s">{data.filiale.fullName}</div>
              </div>
            </div>
            <div className="ds-bp-b" style={{ padding: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
                <KV2 label="Coût total mois"     value={`${fmtInt(data.statsCouts.coutTotalFcfa)} F`} strong />
                <KV2 label="Coût moyen / camion" value={`${fmtInt(data.statsCouts.coutMoyenParCamionFcfa)} F`} />
                <KV2 label="Camions avec BT"     value={String(data.statsCouts.nbCamionsAvecBT)} />
                <KV2 label="Évolution vs M-1"    value={data.statsCouts.evolutionPct !== null ? `${data.statsCouts.evolutionPct >= 0 ? '+' : ''}${data.statsCouts.evolutionPct.toFixed(1)}%` : '—'}
                  color={data.statsCouts.evolutionPct !== null && data.statsCouts.evolutionPct > 0 ? '#dc2626' : '#16a34a'} strong />
                <KV2 label="Taux respect préventif" value={`${data.tauxRespectPreventif.pourcentage.toFixed(1)}%`}
                  color={data.tauxRespectPreventif.pourcentage >= 80 ? '#16a34a' : data.tauxRespectPreventif.pourcentage >= 50 ? '#f59e0b' : '#dc2626'} strong />
                <KV2 label="Planning total"   value={`${data.tauxRespectPreventif.realises} / ${data.tauxRespectPreventif.totalEntries}`} />
              </div>
            </div>
          </div>

          {/* BT par type */}
          <div className="ds-bp" style={{ marginBottom: 14 }}>
            <div className="ds-bp-h"><div><div className="ds-bp-t">Bons de Travail par type</div></div></div>
            <div className="ds-bp-b">
              <table className="ds-tbl">
                <thead><tr><th>Type</th><th className="ds-am">Nombre</th><th className="ds-am">Coût (FCFA)</th></tr></thead>
                <tbody>
                  {data.btParType.map((b) => (
                    <tr key={b.type}>
                      <td style={{ fontWeight: 600 }}>{TYPE_BT_LABELS[b.type]}</td>
                      <td className="ds-am">{b.count}</td>
                      <td className="ds-am" style={{ fontWeight: 700, color: 'var(--p)', fontVariantNumeric: 'tabular-nums' }}>{fmtInt(b.coutFcfa)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Top pannes récurrentes */}
          <div className="ds-bp" style={{ marginBottom: 14 }}>
            <div className="ds-bp-h"><div><div className="ds-bp-t">Top pannes récurrentes</div><div className="ds-bp-s">Pièces utilisées sur ≥ 2 camions distincts (alerte qualité fournisseur)</div></div></div>
            <div className="ds-bp-b">
              {data.topPannesRecurrentes.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#16a34a', fontSize: 13 }}>✅ Aucune pièce récurrente cross-camions</div>
              ) : (
                <table className="ds-tbl">
                  <thead><tr><th>Pièce</th><th className="ds-am">Camions distincts</th><th className="ds-am">Nb fois</th><th className="ds-am">Coût cumul</th></tr></thead>
                  <tbody>
                    {data.topPannesRecurrentes.map((p) => (
                      <tr key={p.designation} style={p.nbCamionsDistincts >= 3 ? { background: 'rgba(239,68,68,0.06)' } : undefined}>
                        <td style={{ fontWeight: 600 }}>{p.designation}</td>
                        <td className="ds-am" style={{ fontWeight: 700, color: p.nbCamionsDistincts >= 3 ? '#dc2626' : 'var(--tx-2)' }}>{p.nbCamionsDistincts}</td>
                        <td className="ds-am">{p.nbFois}</td>
                        <td className="ds-am" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtInt(p.coutCumulFcfa)} F</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Top pièces consommées */}
          <div className="ds-bp" style={{ marginBottom: 14 }}>
            <div className="ds-bp-h"><div><div className="ds-bp-t">Top 10 pièces les plus utilisées</div></div></div>
            <div className="ds-bp-b">
              {data.topPiecesConsommees.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--tx-3)', fontSize: 12 }}>Aucune pièce enregistrée</div>
              ) : (
                <table className="ds-tbl">
                  <thead><tr><th>Pièce</th><th className="ds-am">Nb fois</th><th className="ds-am">Qté totale</th><th className="ds-am">Coût cumul</th></tr></thead>
                  <tbody>
                    {data.topPiecesConsommees.map((p) => (
                      <tr key={p.designation}>
                        <td>{p.designation}</td>
                        <td className="ds-am">{p.nbFois}</td>
                        <td className="ds-am">{p.quantiteTotale}</td>
                        <td className="ds-am" style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: 'var(--p)' }}>{fmtInt(p.coutCumulFcfa)} F</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Camions coût/tonne anormal */}
          {data.camionsCoutTonneAnormal.length > 0 && (
            <div className="ds-bp" style={{ marginBottom: 14 }}>
              <div className="ds-bp-h"><div><div className="ds-bp-t" style={{ color: '#dc2626' }}>⚠️ Camions ratio coût/tonne anormal</div><div className="ds-bp-s">Seuil 2000 = analyse · 3500 = réforme recommandée</div></div></div>
              <div className="ds-bp-b">
                <table className="ds-tbl">
                  <thead><tr><th>Camion</th><th>Modèle</th><th className="ds-am">Coût total</th><th className="ds-am">Tonnes</th><th className="ds-am">Coût/tonne</th><th>Action</th></tr></thead>
                  <tbody>
                    {data.camionsCoutTonneAnormal.map((c) => (
                      <tr key={c.camionCode} style={c.gravite === 'reforme' ? { background: 'rgba(239,68,68,0.08)' } : { background: 'rgba(245,158,11,0.04)' }}>
                        <td style={{ fontWeight: 600 }}>{c.camionCode}</td>
                        <td style={{ fontSize: 12 }}>{c.modele ?? '—'}</td>
                        <td className="ds-am" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtInt(c.coutTotalFcfa)} F</td>
                        <td className="ds-am" style={{ fontVariantNumeric: 'tabular-nums' }}>{c.tonnesMois.toFixed(1)} T</td>
                        <td className="ds-am" style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: '#dc2626' }}>{fmtInt(c.coutParTonne)} F/T</td>
                        <td style={{ fontSize: 11, fontWeight: 700, color: c.gravite === 'reforme' ? '#dc2626' : '#f59e0b' }}>
                          {c.gravite === 'reforme' ? '🔴 RÉFORME' : '🟠 ANALYSE'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

const KV2 = ({ label, value, color, strong }: { label: string; value: string; color?: string; strong?: boolean }) => (
  <div>
    <div style={{ fontSize: 11, color: 'var(--tx-3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
    <div style={{ fontSize: strong ? 18 : 14, fontWeight: strong ? 700 : 500, color: color ?? 'var(--tx-1)', fontVariantNumeric: 'tabular-nums', letterSpacing: 'normal' }}>{value}</div>
  </div>
);

// ── Onglet Planning préventif ──────────────────────────────────────────────

const PlanningTab = () => {
  const role = useAuthStore((s) => s.user?.role);
  const canGenerate = can(role, 'operations.manage');

  const { data: alertes = [], isLoading: loadingAlertes, refetch: refetchAlertes } = useAlertesPlanning();
  const [niveau, setNiveau] = useState<NiveauBonTravail | ''>('');
  const [statut, setStatut] = useState<StatutPlanningPreventif | ''>('');
  const { data: planningData, isLoading: loadingPlanning } = usePlanning({
    niveau: niveau || undefined,
    statut: statut || undefined,
    limit:  200,
  });
  const generer  = useGenererBTPlanning();
  const annuler  = useAnnulerPlanning();
  const trigN2   = useTriggerN2Hebdo();

  const planning = planningData?.data ?? [];

  const onGenererFromAlerte = async (alerte: typeof alertes[number]) => {
    // On crée d'abord une entrée planning (nécessaire pour générer un BT via le service)
    // Pour MVP : on bypass — on demande au backend d'accepter directement les alertes sans planning intermédiaire.
    // Simplification : on demande au PDG de cliquer sur "Lancer N2 hebdo" qui crée les entrées, puis on génère ensuite.
    // Pour les N3/N4/N5 alertes : on doit faire un POST direct planning_preventif. Le brief MVP suffit avec l'option Trigger N2 ;
    // pour N3/N4/N5 c'est manuel via le tableau "Toutes les entrées".
    // TICKET-2026-004 : la saisie du mécanicien était demandée puis ignorée — retirée ; on informe seulement.
    void alerte;
    informer(
      `Programmer un BT ${NIVEAU_BT_LABELS[alerte.niveau]} pour ${alerte.camionCode}`,
      'Pour une alerte N3/N4/N5, utilisez d\'abord le bouton « Créer un BT » en haut de la page Maintenance. '
        + 'Les BT N2 hebdo sont créés via « Lancer N2 hebdo » (automatique le vendredi à 18h).',
    );
  };

  const onGenererFromPlanning = async (id: string, camionCode: string, niveauP: NiveauBonTravail) => {
    const meca = await demander.texte({ titre: `Générer un BT ${NIVEAU_BT_LABELS[niveauP]} pour ${camionCode}`, libelle: 'Nom du mécanicien principal', confirmer: 'Générer le BT' });
    if (!meca?.trim()) return;
    await generer.mutateAsync({ planningId: id, mecanicienPrincipalNom: meca.trim() });
  };

  const onAnnulerPlanning = async (id: string) => {
    if (!(await demander.confirmer({ titre: 'Annuler cette entrée du planning ?', confirmer: 'Annuler l\'entrée', danger: true }))) return;
    await annuler.mutateAsync(id);
  };

  const onTriggerN2 = async () => {
    if (!(await demander.confirmer({ titre: 'Lancer manuellement la génération des entrées N2 hebdo pour dimanche prochain ?', message: 'Normalement automatique le vendredi à 18h (heure de Conakry).', confirmer: 'Lancer' }))) return;
    const { created } = await trigN2.mutateAsync();
    informer(`${created} entrée(s) N2 hebdo créée(s) pour dimanche prochain.`);
  };

  // KPI alertes par niveau
  const alertesByNiv: Record<string, typeof alertes> = {};
  for (const a of alertes) (alertesByNiv[a.niveau] ??= []).push(a);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 14, color: 'var(--tx-2)' }}>Planning préventif (brief §4.4)</h3>
        <div style={{ flex: 1 }} />
        {canGenerate && (
          <button type="button" onClick={onTriggerN2} className="ds-btn ds-btn-g" disabled={trigN2.isPending} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Zap size={14} /> {trigN2.isPending ? 'Génération…' : 'Lancer N2 hebdo manuellement'}
          </button>
        )}
        <button type="button" onClick={() => refetchAlertes()} className="ds-btn ds-btn-g" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={14} /> Actualiser
        </button>
      </div>

      {/* KPI alertes par niveau */}
      <div className="ds-kpi-row" style={{ marginBottom: 14, gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="ds-kr" style={{ borderLeft: `4px solid ${NIVEAU_COLORS.n3_10000km}` }}>
          <div className="ds-kr-ico">🟠</div>
          <div className="ds-kr-val" style={{ fontVariantNumeric: 'tabular-nums' }}>{alertesByNiv.n3_10000km?.length ?? 0}</div>
          <div className="ds-kr-lbl">Alertes N3 — /10 000 km</div>
        </div>
        <div className="ds-kr" style={{ borderLeft: `4px solid ${NIVEAU_COLORS.n4_50000km}` }}>
          <div className="ds-kr-ico">🔴</div>
          <div className="ds-kr-val" style={{ fontVariantNumeric: 'tabular-nums' }}>{alertesByNiv.n4_50000km?.length ?? 0}</div>
          <div className="ds-kr-lbl">Alertes N4 — /50 000 km</div>
        </div>
        <div className="ds-kr" style={{ borderLeft: `4px solid ${NIVEAU_COLORS.n5_annuel}` }}>
          <div className="ds-kr-ico">🟣</div>
          <div className="ds-kr-val" style={{ fontVariantNumeric: 'tabular-nums' }}>{alertesByNiv.n5_annuel?.length ?? 0}</div>
          <div className="ds-kr-lbl">Alertes N5 — Anniversaire (-30j)</div>
        </div>
        <div className="ds-kr" style={{ borderLeft: `4px solid ${NIVEAU_COLORS.n2_hebdo}` }}>
          <div className="ds-kr-ico">📅</div>
          <div className="ds-kr-val" style={{ fontVariantNumeric: 'tabular-nums' }}>{planning.filter((p) => p.niveau === 'n2_hebdo' && p.statut === 'planifie').length}</div>
          <div className="ds-kr-lbl">N2 hebdo planifiés</div>
        </div>
      </div>

      {/* Alertes live (N3/N4/N5) */}
      <div className="ds-bp" style={{ marginBottom: 14 }}>
        <div className="ds-bp-h">
          <div>
            <div className="ds-bp-t">Alertes maintenance live</div>
            <div className="ds-bp-s">Calculées depuis km_actuel et date_mise_en_service des camions</div>
          </div>
          <AlertOctagon size={16} style={{ color: '#dc2626' }} />
        </div>
        <div className="ds-bp-b">
          {loadingAlertes ? (
            <div style={{ padding: 24, textAlign: 'center' }}>Chargement…</div>
          ) : alertes.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#16a34a', fontSize: 13 }}>
              ✅ Aucune alerte de maintenance préventive active
            </div>
          ) : (
            <table className="ds-tbl">
              <thead>
                <tr>
                  <th>Niveau</th>
                  <th>Camion</th>
                  <th>Modèle</th>
                  <th>Raison</th>
                  <th>Gravité</th>
                  <th className="ds-am">Actions</th>
                </tr>
              </thead>
              <tbody>
                {alertes.map((a) => (
                  <tr key={`${a.camionId}-${a.niveau}`} style={a.gravite === 'critique' ? { background: 'rgba(239,68,68,0.06)' } : undefined}>
                    <td>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 10,
                        fontSize: 10, fontWeight: 700, color: 'white',
                        background: NIVEAU_COLORS[a.niveau],
                      }}>
                        {NIVEAU_BT_LABELS[a.niveau]}
                      </span>
                    </td>
                    <td style={{ fontWeight: 600 }}>{a.camionCode}{a.immat && <span style={{ fontSize: 10, color: 'var(--tx-3)', marginLeft: 4 }}>{a.immat}</span>}</td>
                    <td style={{ fontSize: 12 }}>{a.modele ?? '—'}</td>
                    <td style={{ fontSize: 12 }}>{a.raison}</td>
                    <td style={{ fontSize: 11, textTransform: 'uppercase', fontWeight: 700, color: a.gravite === 'critique' ? '#dc2626' : '#f59e0b' }}>
                      {a.gravite}
                      {a.planningExisteDeja && <span style={{ marginLeft: 6, padding: '1px 6px', background: 'var(--p-soft)', color: 'var(--p)', borderRadius: 4, fontSize: 9 }}>EN PLANNING</span>}
                    </td>
                    <td className="ds-am">
                      <button type="button" className="ds-btn ds-btn-xs ds-btn-p" disabled={a.planningExisteDeja} onClick={() => onGenererFromAlerte(a)}>
                        + Programmer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Toutes les entrées du planning */}
      <div className="ds-bp" style={{ marginBottom: 14 }}>
        <div className="ds-bp-h">
          <div>
            <div className="ds-bp-t">Entrées de planning</div>
            <div className="ds-bp-s">N2 hebdo (auto vendredi 18h Conakry) + entrées N3/N4/N5 créées manuellement</div>
          </div>
          <Calendar size={16} style={{ color: 'var(--p)' }} />
        </div>
        <div className="ds-bp-b" style={{ padding: 12 }}>
          <div className="grid grid-cols-3 gap-3" style={{ marginBottom: 12 }}>
            <Field label="Niveau">
              <select value={niveau} onChange={(e) => setNiveau(e.target.value as NiveauBonTravail | '')} className="ds-fi" style={{ width: '100%' }}>
                <option value="">— Tous —</option>
                {(Object.keys(NIVEAU_BT_LABELS) as NiveauBonTravail[]).map((n) => <option key={n} value={n}>{NIVEAU_BT_LABELS[n]}</option>)}
              </select>
            </Field>
            <Field label="Statut">
              <select value={statut} onChange={(e) => setStatut(e.target.value as StatutPlanningPreventif | '')} className="ds-fi" style={{ width: '100%' }}>
                <option value="">— Tous —</option>
                {(Object.keys(STATUT_PLANNING_LABELS) as StatutPlanningPreventif[]).map((s) => <option key={s} value={s}>{STATUT_PLANNING_LABELS[s]}</option>)}
              </select>
            </Field>
          </div>

          {loadingPlanning ? (
            <div style={{ padding: 24, textAlign: 'center' }}>Chargement…</div>
          ) : planning.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--tx-3)', fontSize: 12 }}>
              Aucune entrée planning. Clique « Lancer N2 hebdo » pour générer les entrées hebdomadaires.
            </div>
          ) : (
            <table className="ds-tbl">
              <thead>
                <tr>
                  <th>Date prévue</th>
                  <th>Niveau</th>
                  <th>Camion</th>
                  <th>Source</th>
                  <th>Statut</th>
                  <th>BT lié</th>
                  <th className="ds-am">Actions</th>
                </tr>
              </thead>
              <tbody>
                {planning.map((p) => (
                  <tr key={p.id}>
                    <td style={{ fontSize: 11, fontFamily: 'var(--font-m)' }}>
                      {new Date(p.datePrevue).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short' })}
                    </td>
                    <td>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 10,
                        fontSize: 10, fontWeight: 700, color: 'white',
                        background: NIVEAU_COLORS[p.niveau],
                      }}>{NIVEAU_BT_LABELS[p.niveau]}</span>
                    </td>
                    <td style={{ fontWeight: 600 }}>{p.camion?.code ?? '—'}</td>
                    <td style={{ fontSize: 11, color: 'var(--tx-3)' }}>{p.source ?? '—'}</td>
                    <td style={{ fontSize: 11 }}>{STATUT_PLANNING_LABELS[p.statut]}</td>
                    <td>{p.bonTravail ? <span className="ds-ref-code">{p.bonTravail.numero}</span> : <span style={{ color: 'var(--tx-3)' }}>—</span>}</td>
                    <td className="ds-am" style={{ whiteSpace: 'nowrap' }}>
                      {p.statut === 'planifie' && canGenerate && (
                        <>
                          <button type="button" className="ds-btn ds-btn-xs ds-btn-p" onClick={() => onGenererFromPlanning(p.id, p.camion?.code ?? '—', p.niveau)} disabled={generer.isPending}>
                            Générer BT
                          </button>
                          <button type="button" className="ds-btn ds-btn-xs ds-btn-g" style={{ marginLeft: 4 }} onClick={() => onAnnulerPlanning(p.id)} disabled={annuler.isPending}>
                            Annuler
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
};

const DashboardTab = () => {
  const { data, isLoading, refetch, dataUpdatedAt } = useMaintenanceDashboard();
  if (isLoading || !data) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--tx-3)' }}>Chargement…</div>;

  return (
    <>
      {data.alertes.length > 0 && (
        <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 14, flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
          {data.alertes.map((a, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <span style={{ fontSize: 16 }}>{a.icone}</span>
              <strong style={{ flex: 1 }}>{a.texte}</strong>
            </div>
          ))}
        </div>
      )}

      <div className="ds-kpi-row" style={{ marginBottom: 14, gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="ds-kr" style={{ borderLeft: '4px solid #f59e0b' }}>
          <div className="ds-kr-ico">🔧</div>
          <div className="ds-kr-val" style={{ fontVariantNumeric: 'tabular-nums' }}>{data.kpis.btOuverts}</div>
          <div className="ds-kr-lbl">BT ouverts</div>
        </div>
        <div className="ds-kr" style={{ borderLeft: '4px solid #3b82f6' }}>
          <div className="ds-kr-ico">📋</div>
          <div className="ds-kr-val" style={{ fontVariantNumeric: 'tabular-nums' }}>{data.kpis.btCeMois}</div>
          <div className="ds-kr-lbl">BT ce mois</div>
        </div>
        <div className="ds-kr" style={{ borderLeft: '4px solid #a855f7' }}>
          <div className="ds-kr-ico">💰</div>
          <div className="ds-kr-val" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtInt(data.kpis.coutMoisFcfa)}</div>
          <div className="ds-kr-lbl">Coût mois (FCFA)</div>
        </div>
        <div className="ds-kr" style={{ borderLeft: '4px solid #10b981' }}>
          <div className="ds-kr-ico">🚛</div>
          <div className="ds-kr-val" style={{ fontVariantNumeric: 'tabular-nums', color: data.kpis.tauxDispoFlottePct < 85 ? '#dc2626' : data.kpis.tauxDispoFlottePct < 90 ? '#f59e0b' : undefined }}>
            {data.kpis.tauxDispoFlottePct.toFixed(1)}%
          </div>
          <div className="ds-kr-lbl">Taux dispo flotte</div>
        </div>
      </div>

      {/* Top 10 coûts par camion */}
      <div className="ds-bp" style={{ marginBottom: 14 }}>
        <div className="ds-bp-h">
          <div>
            <div className="ds-bp-t">Top 10 camions par coût maintenance (mois)</div>
            <div className="ds-bp-s">Seuil réforme : &gt; 2 000 FCFA/tonne · Analyse obligatoire : &gt; 3 500</div>
          </div>
          <Wrench size={16} style={{ color: 'var(--p)' }} />
        </div>
        <div className="ds-bp-b">
          {data.topCoutsCamions.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--tx-3)', fontSize: 12 }}>Aucun BT enregistré ce mois</div>
          ) : (
            <table className="ds-tbl">
              <thead>
                <tr>
                  <th>Rang</th>
                  <th>Camion</th>
                  <th className="ds-am">Nb BT</th>
                  <th className="ds-am">Coût total (FCFA)</th>
                  <th className="ds-am">Coût/tonne</th>
                </tr>
              </thead>
              <tbody>
                {data.topCoutsCamions.map((c, i) => {
                  const alerteReforme = c.coutParTonne !== null && c.coutParTonne > 3500;
                  const alerteAnalyse = c.coutParTonne !== null && c.coutParTonne > 2000;
                  return (
                    <tr key={c.camionCode} style={alerteReforme ? { background: 'rgba(239,68,68,0.08)' } : alerteAnalyse ? { background: 'rgba(245,158,11,0.06)' } : undefined}>
                      <td style={{ fontWeight: 700, color: i < 3 ? '#dc2626' : 'var(--tx-2)' }}>#{i + 1}</td>
                      <td><strong>{c.camionCode}</strong>{c.modele && <div style={{ fontSize: 10, color: 'var(--tx-3)' }}>{c.modele}</div>}</td>
                      <td className="ds-am">{c.nbBT}</td>
                      <td className="ds-am" style={{ fontWeight: 700, color: 'var(--p)', fontVariantNumeric: 'tabular-nums' }}>{fmtInt(c.coutTotalFcfa)}</td>
                      <td className="ds-am" style={{ fontWeight: alerteAnalyse ? 700 : 500, color: alerteReforme ? '#dc2626' : alerteAnalyse ? '#f59e0b' : 'var(--tx-2)', fontVariantNumeric: 'tabular-nums' }}>
                        {c.coutParTonne !== null ? `${fmtInt(c.coutParTonne)} F/T` : '—'}
                        {alerteReforme && ' 🔴'}
                        {!alerteReforme && alerteAnalyse && ' 🟠'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div style={{ padding: 10, borderRadius: 'var(--r-sm)', border: '1px dashed var(--bdr)', background: 'var(--surface)', textAlign: 'center', fontSize: 11, color: 'var(--tx-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
        ● <strong>LIVE</strong> · Actualisé {new Date(dataUpdatedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
        <button type="button" onClick={() => refetch()} style={{ padding: '2px 10px', border: '1px solid var(--bdr)', background: 'transparent', borderRadius: 4, cursor: 'pointer', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <RefreshCw size={11} /> Actualiser
        </button>
      </div>
    </>
  );
};

const BTTab = ({ onEdit }: { onEdit: (bt: BonTravail) => void }) => {
  const [statut, setStatut]     = useState<StatutBonTravail | ''>('');
  const [type, setType]         = useState<TypeBonTravail | ''>('');
  const [camionId, setCamionId] = useState('');

  const { data, isLoading } = useBonsTravail({
    statut:   statut   || undefined,
    type:     type     || undefined,
    camionId: camionId || undefined,
    limit:    200,
  });
  const { data: camionsData } = useCamions({ actif: true, limit: 500 });
  const bts     = data?.data ?? [];
  const camions = camionsData?.data ?? [];

  return (
    <>
      <div className="ds-bp" style={{ marginBottom: 14 }}>
        <div className="ds-bp-b" style={{ padding: 12 }}>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Statut">
              <select value={statut} onChange={(e) => setStatut(e.target.value as StatutBonTravail | '')} className="ds-fi" style={{ width: '100%' }}>
                <option value="">— Tous —</option>
                {(Object.keys(STATUT_BT_LABELS) as StatutBonTravail[]).map((s) => <option key={s} value={s}>{STATUT_BT_LABELS[s]}</option>)}
              </select>
            </Field>
            <Field label="Type">
              <select value={type} onChange={(e) => setType(e.target.value as TypeBonTravail | '')} className="ds-fi" style={{ width: '100%' }}>
                <option value="">— Tous —</option>
                {(Object.keys(TYPE_BT_LABELS) as TypeBonTravail[]).map((t) => <option key={t} value={t}>{TYPE_BT_LABELS[t]}</option>)}
              </select>
            </Field>
            <Field label="Camion">
              <select value={camionId} onChange={(e) => setCamionId(e.target.value)} className="ds-fi" style={{ width: '100%' }}>
                <option value="">— Tous —</option>
                {camions.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
              </select>
            </Field>
          </div>
        </div>
      </div>

      <div className="ds-bp">
        <div className="ds-bp-b">
          {isLoading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--tx-3)' }}>Chargement…</div>
          ) : bts.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--tx-3)' }}>Aucun BT pour ces filtres</div>
          ) : (
            <table className="ds-tbl">
              <thead>
                <tr>
                  <th>N° BT</th>
                  <th>Ouverture</th>
                  <th>Camion</th>
                  <th>Type</th>
                  <th>Lieu</th>
                  <th>Mécanicien</th>
                  <th className="ds-am">Âge</th>
                  <th className="ds-am">Coût (F)</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {bts.map((bt) => {
                  const ageMs = Date.now() - new Date(bt.createdAt).getTime();
                  const isEnCours = bt.statut === 'en_cours';
                  const ageColor = isEnCours && ageMs > 48 * 3600_000 ? '#dc2626' : isEnCours && ageMs > 24 * 3600_000 ? '#f59e0b' : 'var(--tx-3)';
                  return (
                    <tr key={bt.id} onClick={() => onEdit(bt)} style={{ cursor: 'pointer' }}>
                      <td><span className="ds-ref-code">{bt.numero}</span></td>
                      <td style={{ fontSize: 11, fontFamily: 'var(--font-m)' }}>{fmtDate(bt.createdAt)}</td>
                      <td style={{ fontWeight: 600 }}>{bt.camion?.code ?? '—'}</td>
                      <td style={{ fontSize: 12 }}>{TYPE_BT_LABELS[bt.type]}</td>
                      <td style={{ fontSize: 11, color: 'var(--tx-3)' }}>{bt.lieu.replace(/_/g, ' ')}</td>
                      <td style={{ fontSize: 12 }}>{bt.mecanicienPrincipalNom}</td>
                      <td className="ds-am" style={{ fontSize: 11, color: ageColor, fontWeight: ageColor !== 'var(--tx-3)' ? 700 : 400 }}>
                        {ageFromCreated(bt.createdAt)}
                        {isEnCours && ageMs > 48 * 3600_000 && ' 🔴'}
                        {isEnCours && ageMs > 24 * 3600_000 && ageMs <= 48 * 3600_000 && ' 🟠'}
                      </td>
                      <td className="ds-am" style={{ fontWeight: 700, color: 'var(--p)', fontVariantNumeric: 'tabular-nums' }}>
                        {bt.coutTotalFcfa ? fmtInt(parseFloat(bt.coutTotalFcfa)) : '—'}
                      </td>
                      <td>
                        <span style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: 10,
                          fontSize: 10, fontWeight: 700, color: 'white',
                          background: STATUT_BT_COLORS[bt.statut],
                        }}>
                          {STATUT_BT_LABELS[bt.statut]}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div><label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>{children}</div>
);
